export type DateTimeFormatPreference = 'mdy_12' | 'dmy_12' | 'dmy_long_24';

export const DATE_TIME_FORMAT_OPTIONS: { value: DateTimeFormatPreference; label: string }[] = [
  { value: 'mdy_12', label: 'MM/DD/YYYY hh:mm:ss AM/PM' },
  { value: 'dmy_12', label: 'DD/MM/YYYY hh:mm:ss AM/PM' },
  { value: 'dmy_long_24', label: 'DD Month YYYY HH:mm:ss' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (value: number) => value.toString().padStart(2, '0');

const normalizePreference = (pref?: string | null): DateTimeFormatPreference => {
  if (pref === 'dmy_12' || pref === 'dmy_long_24' || pref === 'mdy_12') {
    return pref;
  }
  return 'mdy_12';
};

const parseDateValue = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateTime = (value?: string | Date | null, pref?: string | null) => {
  const date = parseDateValue(value);
  if (!date) return '';
  const format = normalizePreference(pref);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours24 = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  if (format === 'dmy_long_24') {
    return `${pad(day)} ${MONTH_NAMES[month - 1]} ${year} ${pad(hours24)}:${pad(minutes)}:${pad(seconds)}`;
  }
  const hours12 = hours24 % 12 || 12;
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  if (format === 'dmy_12') {
    return `${pad(day)}/${pad(month)}/${year} ${pad(hours12)}:${pad(minutes)}:${pad(seconds)} ${ampm}`;
  }
  return `${pad(month)}/${pad(day)}/${year} ${pad(hours12)}:${pad(minutes)}:${pad(seconds)} ${ampm}`;
};

export const formatDateOnly = (value?: string | Date | null, pref?: string | null) => {
  const date = parseDateValue(value);
  if (!date) return '';
  const format = normalizePreference(pref);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  if (format === 'dmy_long_24') {
    return `${pad(day)} ${MONTH_NAMES[month - 1]} ${year}`;
  }
  if (format === 'dmy_12') {
    return `${pad(day)}/${pad(month)}/${year}`;
  }
  return `${pad(month)}/${pad(day)}/${year}`;
};

export const formatTimeOnly = (
  value?: string | Date | null,
  pref?: string | null,
  includeSeconds = true
) => {
  const date = parseDateValue(value);
  if (!date) return '';
  const format = normalizePreference(pref);
  const hours24 = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  if (format === 'dmy_long_24') {
    return `${pad(hours24)}:${pad(minutes)}${includeSeconds ? `:${pad(seconds)}` : ''}`;
  }
  const hours12 = hours24 % 12 || 12;
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  return `${pad(hours12)}:${pad(minutes)}${includeSeconds ? `:${pad(seconds)}` : ''} ${ampm}`;
};

/** Clock time only (`13:00`, `1:00 PM`) using the user's Date/Time Format. */
export const formatTimeOfDay = (
  value?: string | null,
  pref?: string | null,
  includeSeconds = false
) => {
  if (!value) return '';
  const raw = String(value).trim();
  let hours: number | null = null;
  let minutes = 0;
  const twentyFour = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  const twelve = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (twelve) {
    hours = parseInt(twelve[1], 10);
    minutes = parseInt(twelve[2], 10);
    const ampm = twelve[4].toUpperCase();
    if (ampm === 'PM' && hours !== 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;
  } else if (twentyFour) {
    hours = parseInt(twentyFour[1], 10);
    minutes = parseInt(twentyFour[2], 10);
  }
  if (hours === null || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return raw;
  }
  const date = new Date(2000, 0, 1, hours, minutes, 0);
  return formatTimeOnly(date, pref, includeSeconds) || raw;
};

// A bare "HH:mm" clock string, as a person reads it.
//
// The slot endpoints speak 24-hour ("13:00") because that is how the
// availability service stores a working day. formatSlotLabel formatted the date
// and passed the time straight through, so every slot in the panel — and every
// slot in the message the office texts the patient — read as "13:00".
//
// Not a timezone concern: these are already the clinic's wall clock. Shifting
// them would move real appointments.
export const formatClockString = (hhmm?: string | null, pref?: string | null): string => {
  const match = String(hhmm || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return String(hhmm || '');
  const hours24 = Number(match[1]);
  if (hours24 > 23) return String(hhmm);
  // The one preference that genuinely wants a 24-hour clock keeps it.
  if (normalizePreference(pref) === 'dmy_long_24') {
    return `${pad(hours24)}:${match[2]}`;
  }
  const suffix = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${match[2]} ${suffix}`;
};
